import { useCurrentLayout, useCurrentRouteState } from '@pbk-web/state';
import type { FCWithChildren } from '@pbk-web/types';
import { Layouts } from '@pbk-web/types';
import { PageLoader } from '@pbk-web/ui';
import { lazy, Suspense, useEffect, useMemo } from 'react';

import { useCurrentUser } from '../global-state/Auth';

const AuthLayout = lazy(() => import('./AuthLayout'));
const LoggedInLayout = lazy(() => import('./LoggedInLayout'));
const MinimalLayout = lazy(() => import('./MinimalLayout'));
const PublicLayout = lazy(() => import('./PublicLayout'));

type LayoutProps = {
  layout: keyof typeof Layouts;
};

export const Layout: FCWithChildren<LayoutProps> = (props) => {
  const { layout, children } = props;
  const layoutValue = Layouts[layout];
  const setCurrentLayout = useCurrentLayout((x) => x.setValue);

  useEffect(() => {
    setCurrentLayout(layout);
    return () => setCurrentLayout(null);
  }, [layout, setCurrentLayout]);

  const component = useMemo(() => {
    switch (layoutValue) {
      case Layouts.LoggedIn:
        return <LoggedInLayout key="loggedInLayout">{children}</LoggedInLayout>;
      case Layouts.Public:
        return <PublicLayout>{children}</PublicLayout>;
      case Layouts.Auth:
        return <AuthLayout>{children}</AuthLayout>;
      case Layouts.Minimal:
        return <MinimalLayout>{children}</MinimalLayout>;
      default:
        throw new Error('Unknown layout ' + layout);
    }
  }, [children, layout, layoutValue]);

  return <Suspense fallback={<PageLoader loading isSuspense />}>{component}</Suspense>;
};

export const AutoLayout: FCWithChildren = (props) => {
  const { children } = props;
  const route = useCurrentRouteState((x) => x.value);
  const currentUser = useCurrentUser((x) => x.value);

  if (route?.layout) {
    return <Layout layout={route.layout}>{children}</Layout>;
  }

  if (route?.publicPage) {
    return <Layout layout="Public">{children}</Layout>;
  }

  return <Layout layout={currentUser ? 'LoggedIn' : 'Public'}>{children}</Layout>;
};
